/**
 * WorkspaceShell — mirrors B2B Sidebar.tsx / Topbar.tsx structure.
 *
 *  Desktop layout:
 *  ┌─────────────────┬──────────────────────────────────────────────────┐
 *  │ GoGrabJob chip  │  breadcrumb            [credits] [bell] [avatar] │
 *  │ ─────────────── │ ──────────────────────────────────────────────── │
 *  │ ● Home          │                                                   │
 *  │   Jobs          │         <main>{children}</main>                  │
 *  │   Agent         │                                                   │
 *  │   Activity      │                                                   │
 *  │                 │                                                   │
 *  │ MORE ─────────  │                                                   │
 *  │   Billing       │                                                   │
 *  │   ...           │                                                   │
 *  │                 │                                                   │
 *  │ COLLECTIONS ─── │                                                   │
 *  │   My saved      │                                                   │
 *  │ ─────────────── │                                                   │
 *  │ Invite friends  │                                                   │
 *  │ Help            │                                                   │
 *  │ Account settings│                                                   │
 *  └─────────────────┴──────────────────────────────────────────────────┘
 */
import {
  Home,
  Briefcase,
  Activity,
  Sparkles,
  User as UserIcon,
  X as XIcon,
  Gift,
  PanelLeft,
  ChevronDown,
  ChevronRight as ChevronRightIcon,
  ChevronsLeft,
  Settings,
  HelpCircle as _HelpCircle,
  Bookmark,
  Plus,
  Pencil,
  Trash2,
  Check,
  X as XSmall,
  BookOpen as _BookOpen,
  UserPlus as _UserPlus,
  Video,
  LifeBuoy,
  MessageSquare,
  Layers,
  History,
} from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { featureFlags } from '../../config/environment';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { GGJLogoFull, GGJLogoMark } from '../../components/Brand/GGJLogo';
import { BRAND_LOGO_DISPLAY } from '../../config/brandLogos';
import LoadingSpinner from '../../components/LoadingSpinner';
import { useProductTour } from '../../hooks/useProductTour';
import { FORCE_PRODUCT_TOUR_EVENT, TOUR_OPEN_MOBILE_NAV } from '../../lib/productTourSteps';
import type { DashboardPage } from '../dashboard/components/Layout';

import ProfileCompletionWidget, {
  PROFILE_COMPLETION_NAV_EVENT,
} from '../dashboard/components/ProfileCompletenessMeter';
import { useUserProfile } from '../dashboard/hooks/useUserProfile';
import UtilityCluster from './components/UtilityCluster';
import type { WorkspaceNavId } from './types';
import { dashboardPageToNavId, navIdToDashboardPage, shouldShowGreeting } from './types';
import GreetingBlock from './components/GreetingBlock';

import {
  GGJ_AGENT_NEW_SESSION,
  GGJ_AGENT_TOGGLE_HISTORY,
} from '../ai-agent/components/AgentSidebarPanel';
import { readSidebarCollapsed, SIDEBAR_COLLAPSED_LS_KEY } from './sidebarState';

const COLLECTIONS_LS_KEY = 'ggj_job_collections'; // shared with AgentJobsPanel

/** Collapsed rail — icon-only (labels hidden; tooltips via title / aria-label) */
const SIDEBAR_WIDTH_COLLAPSED_PX = 52;
const SIDEBAR_WIDTH_EXPANDED_PX = 220;

// ── Nav definitions ───────────────────────────────────────────────────────────

/** Core primary items — always visible at top of sidebar */
const PRIMARY_ITEMS = [
  { id: 'home'      as WorkspaceNavId, labelKey: 'shell.nav.home',      fallback: 'Home',      icon: Home,      dataTour: 'home-nav'      },
  { id: 'jobs'      as WorkspaceNavId, labelKey: 'shell.nav.jobs',      fallback: 'Jobs',      icon: Briefcase, dataTour: 'jobs-nav'      },
  // Legacy 'optimizer' rail entry removed — optimization is now performed by
  // the AI Agent below. Don't re-add this row; see
  // src/_deadcode/SingleOptimizeFlow/README.md for context.
  { id: 'agent'     as WorkspaceNavId, labelKey: 'shell.nav.agent',     fallback: 'Agent',     icon: Sparkles,  dataTour: 'agent-nav'     },
];

/** Collapsed sidebar top rail — Activity stays in middle rail (secondary suite). */
const COLLAPSED_TOP_RAIL_NAV_IDS: WorkspaceNavId[] = ['home', 'jobs', 'agent'];

interface SidebarItem {
  id: string;
  page: DashboardPage;
  labelKey: string;
  fallback: string;
  icon: React.ComponentType<{ className?: string }>;
  path?: string;
  adminOnly?: boolean;
  editorOnly?: boolean;
  dataTour?: string;
}

/** Feature items — always visible below primary items (no MORE accordion) */
const FEATURE_ITEMS: SidebarItem[] = [
  { id: 'prepare-for-interview',page: 'prepare-for-interview',labelKey: 'shell.feature.interviews',      fallback: 'Prepare',          icon: Video,         dataTour: 'tour-interview-prep'     },
  { id: 'activity',             page: 'history',              labelKey: 'shell.nav.activity',            fallback: 'Activity',         icon: Activity,      dataTour: 'activity-nav'            },
];

type MoreItem = SidebarItem;

// ── Portal tooltip for collapsed sidebar items ────────────────────────────────


// ── Sidebar nav item — mirrors B2B navItemClass exactly ───────────────────────
// B2B: h-[30px], rounded-[8px], text-[13px] font-medium,
//      active → vscode-bg-tertiary vscode-text-primary (NO left border)
//      inactive → vscode-text-secondary hover:vscode-bg-hover hover:vscode-text-primary

function SidebarNavItem({
  label,
  icon: Icon,
  active,
  onClick,
  dataTour,
  collapsed = false,
}: {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  active: boolean;
  onClick: () => void;
  dataTour?: string;
  collapsed?: boolean;
}) {
  if (collapsed) {
    return (
      <button
        type="button"
        onClick={onClick}
        data-tour={dataTour}
        aria-current={active ? 'page' : undefined}
        aria-label={label}
        title={label}
        className={[
          'ds-workspace-nav-item is-collapsed',
          active ? 'is-active' : '',
        ].join(' ')}
      >
        <Icon className="w-[18px] h-[18px] shrink-0" aria-hidden />
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      data-tour={dataTour}
      aria-current={active ? 'page' : undefined}
      className={[
        'ds-workspace-nav-item gap-2.5 px-3 min-h-[44px] touch-manipulation',
        active ? 'is-active' : '',
      ].join(' ')}
    >
      <Icon className="w-5 h-5 shrink-0" aria-hidden />
      <span className="truncate whitespace-nowrap">{label}</span>
    </button>
  );
}

// ── Collections (mirrors B2B SidebarCollections) ──────────────────────────────
// Stored under 'ggj_job_collections' in localStorage (shared with AgentJobsPanel).
// Items: { id, name, jobIds[] }
// On hover: rename (inline) + delete icons.

interface CollectionItem {
  id: string;
  name: string;
  jobIds: string[];
}

function readCollections(): CollectionItem[] {
  try {
    const raw = localStorage.getItem(COLLECTIONS_LS_KEY);
    if (!raw) return [];
    const p = JSON.parse(raw);
    return Array.isArray(p) ? p : [];
  } catch { return []; }
}

function writeCollections(items: CollectionItem[]) {
  localStorage.setItem(COLLECTIONS_LS_KEY, JSON.stringify(items));
  window.dispatchEvent(new CustomEvent('ggj:collections-changed'));
}

function SidebarCollections({ onNavigate }: { onNavigate: (page: DashboardPage) => void }) {
  const [items, setItems] = useState<CollectionItem[]>(() => readCollections());
  const [open, setOpen] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const editInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const sync = () => setItems(readCollections());
    window.addEventListener('ggj:collections-changed', sync);
    window.addEventListener('storage', sync);
    window.addEventListener('focus', sync);
    return () => {
      window.removeEventListener('ggj:collections-changed', sync);
      window.removeEventListener('storage', sync);
      window.removeEventListener('focus', sync);
    };
  }, []);

  useEffect(() => {
    if (editingId) editInputRef.current?.focus();
  }, [editingId]);

  const startRename = (item: CollectionItem) => { setEditingId(item.id); setEditValue(item.name); };
  const commitRename = (id: string) => {
    writeCollections(items.map(i => i.id === id ? { ...i, name: editValue.trim() || i.name } : i));
    setEditingId(null);
  };
  const handleDelete = (id: string) => writeCollections(items.filter(i => i.id !== id));

  return (
    <div className="w-full">
      {/* One padded row: same inset as SidebarNavItem (single px-2 shell, h-[30px]) */}
      <div
        className="group mb-1 flex h-[30px] w-full items-center gap-1 rounded-[8px] px-2 ds-workspace-nav-item"
      >
        <button
          type="button"
          onClick={() => setOpen(v => !v)}
          className="flex min-h-0 min-w-0 flex-1 items-center gap-2 rounded-[8px] text-left t-body outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--ds-brand)_45%,transparent)]"
          style={{ color: 'rgba(255,255,255,0.76)' }}
          aria-expanded={open}
        >
          <Layers className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
          <span className="min-w-0 truncate">Collections</span>
          {items.length > 0 && (
            <span
              className="shrink-0 rounded-full px-1.5 t-tag"
              style={{ background: 'rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.78)' }}
            >
              {items.length}
            </span>
          )}
          {open ? (
            <ChevronDown className="ml-auto h-3.5 w-3.5 shrink-0 opacity-50" aria-hidden />
          ) : (
            <ChevronRightIcon className="ml-auto h-3.5 w-3.5 shrink-0 opacity-50" aria-hidden />
          )}
        </button>
        <button
          type="button"
          onClick={() => onNavigate('agent')}
          className="flex h-7 w-7 shrink-0 items-center justify-center ds-workspace-icon-button"
          style={{ color: 'rgba(255,255,255,0.72)' }}
          title="Create collection"
          aria-label="Create collection"
        >
          <Plus className="h-3.5 w-3.5" aria-hidden />
        </button>
      </div>

      {open && (
        <div className="flex flex-col gap-0.5">
          {items.length === 0 ? (
            <button type="button" onClick={() => onNavigate('agent')}
              className="px-2 py-1 text-left t-tag italic rounded-md transition-colors"
              style={{ color: 'rgba(255,255,255,0.55)' }}>
              No collections yet — create one in Agent
            </button>
          ) : (
            items.map((item) => {
              const isEditing = editingId === item.id;
              return (
                <div key={item.id} className="group relative">
                  {isEditing ? (
                    <div className="flex h-[30px] items-center gap-1 rounded-[8px] px-2">
                      <Bookmark className="h-4 w-4 shrink-0 opacity-40" aria-hidden />
                      <input
                        ref={editInputRef}
                        type="text"
                        value={editValue}
                        onChange={e => setEditValue(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') commitRename(item.id);
                          if (e.key === 'Escape') setEditingId(null);
                        }}
                        className="min-w-0 flex-1 rounded bg-transparent t-meta outline-none ring-1 ring-emerald-500/60 px-1"
                        style={{ color: 'rgba(255,255,255,0.88)' }}
                        aria-label="Rename collection"
                      />
                      <button type="button" onClick={() => commitRename(item.id)}
                        className="shrink-0 rounded p-0.5 text-emerald-600 hover:bg-emerald-500/10">
                        <Check className="h-3.5 w-3.5" />
                      </button>
                      <button type="button" onClick={() => setEditingId(null)}
                        className="shrink-0 rounded p-0.5 ds-workspace-icon-button">
                        <XSmall className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex h-[30px] items-center rounded-[8px]">
                      <button type="button" onClick={() => onNavigate('agent')}
                        className="flex min-w-0 flex-1 h-full items-center gap-2 rounded-[8px] px-2 ds-workspace-nav-item"
                        style={{ color: 'rgba(255,255,255,0.72)' }}
                        title={item.name}>
                        <Bookmark className="h-4 w-4 shrink-0" aria-hidden />
                        <span className="min-w-0 flex-1 truncate">{item.name}</span>
                        <span className="t-tag shrink-0" style={{ color: 'rgba(255,255,255,0.55)' }}>{item.jobIds.length}</span>
                      </button>
                      {/* Hover actions — rename + delete (mirrors B2B) */}
                      <div className="flex shrink-0 items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity pr-1">
                        <button type="button" onClick={() => startRename(item)}
                          className="rounded p-1 ds-workspace-icon-button transition-colors"
                          title="Rename">
                          <Pencil className="h-3 w-3" />
                        </button>
                        <button type="button" onClick={() => handleDelete(item.id)}
                          className="rounded p-1 ds-workspace-icon-button transition-colors"
                          title="Delete">
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

// ── Sidebar footer (mirrors B2B SidebarFooter) ────────────────────────────────
// B2B: Invite team · Knowledge hub · Account settings
// B2C: Invite friends (referral) · Help · Account settings

function SidebarFooter({
  onNavChange,
  onSignOutClick,
  variant = 'desktop',
}: {
  onNavChange: (page: DashboardPage) => void;
  onSignOutClick: () => void;
  variant?: 'desktop' | 'mobile';
}) {
  const { t } = useTranslation();
  const linkClass =
    'ds-workspace-nav-item flex min-h-[44px] w-full items-center gap-2.5 px-3 touch-manipulation';

  return (
    <div
      className={[
        'flex shrink-0 flex-col gap-0.5 px-3 pt-2',
        variant === 'mobile'
          ? 'pb-[max(0.75rem,env(safe-area-inset-bottom))]'
          : 'pb-3',
      ].join(' ')}
    >
      {featureFlags.enableReferrals && (
        <button
          type="button"
          data-tour="tour-referrals"
          onClick={() => {
            window.history.pushState({}, '', '/referrals');
            window.dispatchEvent(new PopStateEvent('popstate'));
          }}
          className={linkClass}
        >
          <Gift className="h-4 w-4 shrink-0" aria-hidden />
          <span className="truncate">{t('shell.more.referrals', 'Referrals')}</span>
        </button>
      )}
      <button type="button" onClick={() => onNavChange('product-feedback')} className={linkClass}>
        <MessageSquare className="h-4 w-4 shrink-0" aria-hidden />
        <span className="truncate">{t('shell.nav.productFeedback', 'Share feedback')}</span>
      </button>
      <button type="button" onClick={() => onNavChange('support')} className={linkClass}>
        <LifeBuoy className="h-4 w-4 shrink-0" aria-hidden />
        <span className="truncate">{t('shell.nav.supportTicket', 'Support Ticket')}</span>
      </button>
      <button
        type="button"
        data-tour="tour-account-settings"
        onClick={() => onNavChange('account-settings')}
        className={linkClass}
      >
        <Settings className="h-4 w-4 shrink-0" aria-hidden />
        <span className="truncate">Settings</span>
      </button>
    </div>
  );
}

// ── Page title for topbar breadcrumb (simple, mirrors B2B) ────────────────────
const PAGE_TITLE_KEYS: Partial<Record<DashboardPage, string>> = {
  dashboard: 'dashboard.pageTitles.dashboard',
  jobs: 'dashboard.pageTitles.jobs',
  'jobs-admin': 'dashboard.pageTitles.jobsAdmin',
  'single-optimize': 'dashboard.pageTitles.single-optimize',
  history: 'dashboard.pageTitles.history',
  'prepare-for-interview': 'dashboard.pageTitles.prepare-for-interview',
  'ai-companion': 'dashboard.pageTitles.ai-companion',
  'calendar-notes': 'dashboard.pageTitles.calendar-notes',
  agent: 'dashboard.pageTitles.agent',
  profile: 'dashboard.pageTitles.profile',
  'account-settings': 'dashboard.pageTitles.profile',
  referrals: 'dashboard.pageTitles.referrals',
  developer: 'dashboard.pageTitles.developer',
  'payment-history': 'dashboard.pageTitles.payment-history',
  'skill-assessment': 'dashboard.pageTitles.skill-assessment',
  support: 'dashboard.pageTitles.support',
  'product-feedback': 'dashboard.pageTitles.productFeedback',
  'admin-analytics': 'dashboard.pageTitles.admin-analytics',
  'blog-cms': 'dashboard.pageTitles.blog-cms',
  'careers-cms': 'dashboard.pageTitles.careers-cms',
};

const PAGE_TITLES: Partial<Record<DashboardPage, string>> = {
  dashboard: 'Dashboard',
  jobs: '',
  'jobs-admin': 'Job Board',
  // Legacy single-optimize tab label — kept so any lingering reference still
  // resolves to a sensible string, but the route itself redirects to /agent.
  'single-optimize': 'AI Agent',
  history: 'Activity',
  'prepare-for-interview': 'Prepare for Interview',
  'ai-companion': 'AI Companion',
  'calendar-notes': 'Calendar & Notes',
  agent: 'AI Agent',
  profile: 'Account Settings',
  'account-settings': 'Account Settings',
  referrals: 'Refer & earn',
  developer: 'Developer Settings',
  'payment-history': 'Billing',
  'skill-assessment': 'Skill Assessment',
  support: 'Support',
  'product-feedback': 'Product Feedback',
  'admin-analytics': 'Analytics',
  'blog-cms': 'Blog CMS',
  'careers-cms': 'Careers CMS',
};

// ── WorkspaceShell Props ──────────────────────────────────────────────────────

export interface WorkspaceShellProps {
  children: React.ReactNode;
  currentPage: DashboardPage;
  activeNavId?: WorkspaceNavId;
  showGreeting?: boolean;
  onNavChange: (page: DashboardPage) => void;
  userName: string | null;
  userEmail: string;
  userAvatarUrl?: string | null;
  credits: number | null;
  creditsLoading: boolean;
  onUpgradeClick: () => void;
  onSignOutClick: () => void;
  isBlogEditor?: boolean;
  isOwnerAdmin?: boolean;
  onHeroSearch?: (trimmedQuery: string) => void;
  /**
   * When true, the main content area uses overflow-hidden instead of
   * overflow-y-auto. Use for full-bleed pages (agent, account-settings) that
   * manage their own internal scrolling.
   */
  fullBleed?: boolean;
}

// ── Main component ────────────────────────────────────────────────────────────

const WorkspaceShell: React.FC<WorkspaceShellProps> = ({
  children,
  currentPage,
  activeNavId,
  showGreeting,
  onNavChange,
  userName,
  userEmail,
  userAvatarUrl,
  credits,
  creditsLoading,
  onUpgradeClick,
  onSignOutClick,
  onHeroSearch,
  fullBleed = false,
  isOwnerAdmin = false,
}) => {
  const { t } = useTranslation();
  // journey queue is now rendered inline in the header — no portal hosts needed
  const [isDesktop, setIsDesktop] = useState(() => typeof window !== 'undefined' && window.innerWidth >= 1024);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => readSidebarCollapsed());
  useEffect(() => {
    try { localStorage.setItem(SIDEBAR_COLLAPSED_LS_KEY, sidebarCollapsed ? 'true' : 'false'); } catch { /* ignore */ }
  }, [sidebarCollapsed]);

  useEffect(() => {
    const onResize = () => {
      setIsDesktop(window.innerWidth >= 1024);
      if (window.innerWidth < 1024) setSidebarCollapsed(true);
    };
    window.addEventListener('resize', onResize, { passive: true });
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const handleNavChange = useCallback((page: DashboardPage) => {
    onNavChange(page);
    setMobileMenuOpen(false);
  }, [onNavChange]);

  const { forceStart: forceProductTour, isActive: isTourActive, profile: tourProfile, isProfileLoading: tourProfileLoading } = useProductTour({
    onNavigate: handleNavChange,
  });

  useEffect(() => {
    const open = () => setMobileMenuOpen(true);
    window.addEventListener(TOUR_OPEN_MOBILE_NAV, open);
    return () => window.removeEventListener(TOUR_OPEN_MOBILE_NAV, open);
  }, []);

  // Agent / jobs panel requests collapse so chat + canvas have more horizontal space
  useEffect(() => {
    const onSidebar = (e: Event) => {
      const { collapsed } = (e as CustomEvent<{ collapsed?: boolean }>).detail ?? {};
      if (typeof collapsed === 'boolean') setSidebarCollapsed(collapsed);
    };
    window.addEventListener('ggj:workspace-sidebar', onSidebar);
    return () => window.removeEventListener('ggj:workspace-sidebar', onSidebar);
  }, []);

  // e.g. window.restartAppTour() from legacy TourProvider / OnboardingTab
  useEffect(() => {
    const onForce = (): void => {
      forceProductTour();
    };
    window.addEventListener(FORCE_PRODUCT_TOUR_EVENT, onForce);
    return () => window.removeEventListener(FORCE_PRODUCT_TOUR_EVENT, onForce);
  }, [forceProductTour]);

  const [showTourPrompt, setShowTourPrompt] = useState(false);

  // Show the tour prompt ONLY once — right after onboarding completes.
  // If the user dismisses it ("Maybe later") or completes the tour, never show again.
  useEffect(() => {
    const canTryAutoTour =
      (currentPage === 'dashboard' || currentPage === 'agent') && !tourProfileLoading &&
      tourProfile != null && Boolean(tourProfile.id) &&
      !tourProfile.tourCompleted && !isTourActive;
    if (!canTryAutoTour) return;

    // If user already dismissed the prompt once, never show it again
    try {
      if (localStorage.getItem('ggj:tour-prompt-dismissed')) return;
    } catch { /* best-effort */ }

    // Check if onboarding just completed — only then show the tour prompt
    const onboardingJustCompleted = sessionStorage.getItem('onboarding_just_completed');
    if (onboardingJustCompleted) {
      sessionStorage.removeItem('onboarding_just_completed');
      // Show the prompt (not auto-start) after a brief delay for the page to render
      const timer = setTimeout(() => setShowTourPrompt(true), 1200);
      return () => clearTimeout(timer);
    }

    // For all other cases (returning user, page navigation) — do NOT show the prompt
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, tourProfileLoading, tourProfile?.tourCompleted, tourProfile?.id, isTourActive, forceProductTour]);

  // Dismiss the welcome prompt the moment the tour actually starts.
  useEffect(() => {
    if (isTourActive) setShowTourPrompt(false);
  }, [isTourActive]);

  useEffect(() => {
    if (!mobileMenuOpen || isDesktop) return;
    const prevBody = document.body.style.overflow;
    const prevHtml = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevBody;
      document.documentElement.style.overflow = prevHtml;
    };
  }, [mobileMenuOpen, isDesktop]);

  useEffect(() => {
    if (!mobileMenuOpen || isDesktop) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileMenuOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [mobileMenuOpen, isDesktop]);

  const resolvedNavId: WorkspaceNavId = activeNavId ?? dashboardPageToNavId(currentPage);
  const greetingVisible = showGreeting ?? shouldShowGreeting(currentPage);
  const firstName = (userName || '').trim().split(/\s+/)[0] || null;

  // Dynamic sub-title for agent page breadcrumb (e.g. "New search" / session title)
  const [agentSubTitle, setAgentSubTitle] = useState('');
  useEffect(() => {
    const h = (e: Event) => setAgentSubTitle((e as CustomEvent<{ title: string }>).detail?.title ?? '');
    window.addEventListener('ggj:agent-breadcrumb', h);
    return () => window.removeEventListener('ggj:agent-breadcrumb', h);
  }, []);
  // Clear sub-title when navigating away from agent
  useEffect(() => {
    if (currentPage !== 'agent') setAgentSubTitle('');
  }, [currentPage]);

  const featureItems = FEATURE_ITEMS; // always visible (e.g. Cohort uses /cohort path)

  const handlePrimaryNav = (id: WorkspaceNavId) => onNavChange(navIdToDashboardPage(id));

  const { profile: completionProfile, isLoading: completionProfileLoading } = useUserProfile();
  const [agentCanvasOpen, setAgentCanvasOpen] = useState(false);

  useEffect(() => {
    const onAgentPanel = (e: Event) => {
      const { canvas } = (e as CustomEvent<{ active?: boolean; canvas?: boolean }>).detail ?? {};
      setAgentCanvasOpen(Boolean(canvas));
    };
    window.addEventListener('ggj:agent-right-panel', onAgentPanel);
    return () => window.removeEventListener('ggj:agent-right-panel', onAgentPanel);
  }, []);

  useEffect(() => {
    if (currentPage !== 'agent') setAgentCanvasOpen(false);
  }, [currentPage]);

  const handleProfileCompletionNavigate = useCallback(
    (subTab: 'work' | 'preferences' | 'resume', sectionKey?: string) => {
      onNavChange('account-settings');
      requestAnimationFrame(() => {
        window.dispatchEvent(
          new CustomEvent(PROFILE_COMPLETION_NAV_EVENT, { detail: { subTab, sectionKey } }),
        );
      });
    },
    [onNavChange],
  );

  const handleMoreNav = (item: MoreItem) => {
    if (item.path) {
      if (window.location.pathname !== item.path) {
        window.history.pushState({}, '', item.path);
        window.dispatchEvent(new PopStateEvent('popstate'));
      }
      return;
    }
    onNavChange(item.page);
  };

  const sidebarWidth = sidebarCollapsed
    ? `${SIDEBAR_WIDTH_COLLAPSED_PX}px`
    : `${SIDEBAR_WIDTH_EXPANDED_PX}px`;

  const pageTitleKey = PAGE_TITLE_KEYS[currentPage];
  const pageTitle = pageTitleKey
    ? t(pageTitleKey, PAGE_TITLES[currentPage] ?? '')
    : (PAGE_TITLES[currentPage] ?? '');

  return (
    <div
      className={[
        'ds-workspace-shell h-dvh flex overflow-hidden',
        mobileMenuOpen && !isDesktop ? 'ds-workspace-shell--mobile-nav-open' : '',
      ].filter(Boolean).join(' ')}
      data-tour="dashboard-main"
    >

      {/* ═══════════════════════════════════════════════════════════════════
          DESKTOP LEFT SIDEBAR — mirrors B2B <Sidebar>
          ═══════════════════════════════════════════════════════════════════ */}
      <motion.aside
        initial={{ opacity: 0, x: -16 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
        className="ds-workspace-sidebar hidden lg:flex flex-col flex-shrink-0 border-r h-full transition-[width] duration-200 ease-in-out overflow-hidden"
        style={{
          width: sidebarWidth,
          backgroundColor: '#1E2139',
        }}
      >
        <div
          className={`flex-shrink-0 flex items-center gap-2 border-b ${
            sidebarCollapsed
              ? 'flex-col justify-center px-1 py-2 gap-1.5'
              : 'justify-between px-3 py-2.5'
          }`}
          style={{ borderColor: 'rgba(255,255,255,0.08)' }}
        >
          {sidebarCollapsed ? (
            <span
              className="ds-workspace-sidebar-brand flex shrink-0 items-center justify-center"
              style={{ width: BRAND_LOGO_DISPLAY.sidebarCollapsed, height: BRAND_LOGO_DISPLAY.sidebarCollapsed }}
              aria-hidden
            >
              <GGJLogoMark size={BRAND_LOGO_DISPLAY.sidebarCollapsed} />
            </span>
          ) : (
            <GGJLogoFull
              iconSize={BRAND_LOGO_DISPLAY.sidebarExpandedIcon}
              fontSize={BRAND_LOGO_DISPLAY.sidebarExpandedFont}
              textColor="rgba(255,255,255,0.95)"
              className="min-w-0 flex-1"
            />
          )}
          <button
            type="button"
            onClick={() => setSidebarCollapsed(v => !v)}
            className={`rounded-[8px] ds-workspace-icon-button hidden lg:flex items-center justify-center shrink-0 ${
              sidebarCollapsed ? 'h-9 w-9' : 'p-1.5'
            }`}
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {sidebarCollapsed
              ? <PanelLeft className="h-4 w-4 shrink-0" aria-hidden />
              : <PanelLeft className="h-4 w-4 shrink-0 rotate-180" aria-hidden />
            }
          </button>
        </div>

        <nav
          className={`min-h-0 flex-1 overflow-x-hidden flex flex-col ${sidebarCollapsed ? 'pt-1 pb-2' : 'px-3 pb-2 gap-1 overflow-y-auto'}`}
          data-tour="sidebar-navigation"
          aria-label={t('shell.aria.primaryNav', 'Primary navigation')}
        >
          {sidebarCollapsed ? (
            <div className="flex min-h-0 flex-1 flex-col">
              <div className="flex flex-col items-center gap-0.5 px-0.5">
                {PRIMARY_ITEMS.map(item => (
                  <SidebarNavItem
                    key={item.id}
                    label={t(item.labelKey, item.fallback)}
                    icon={item.icon}
                    active={resolvedNavId === item.id}
                    collapsed
                    dataTour={item.dataTour}
                    onClick={() => handlePrimaryNav(item.id)}
                  />
                ))}
              </div>

              <div className="flex min-h-0 flex-1 flex-col items-center gap-0.5 overflow-y-auto overflow-x-hidden px-0.5">
                {featureItems.map(item => (
                  <SidebarNavItem
                    key={item.id}
                    label={t(item.labelKey, item.fallback)}
                    icon={item.icon}
                    active={item.path ? window.location.pathname === item.path : currentPage === item.page}
                    collapsed
                    dataTour={item.dataTour}
                    onClick={() => handleMoreNav(item)}
                  />
                ))}
              </div>

              <div className="mt-auto flex shrink-0 flex-col items-center gap-0.5 px-0.5 pt-1.5">
                {featureFlags.enableReferrals && (
                  <SidebarNavItem
                    label={t('shell.more.referrals', 'Referrals')}
                    icon={Gift}
                    active={typeof window !== 'undefined' && window.location.pathname === '/referrals'}
                    collapsed
                    dataTour="tour-referrals"
                    onClick={() => {
                      window.history.pushState({}, '', '/referrals');
                      window.dispatchEvent(new PopStateEvent('popstate'));
                    }}
                  />
                )}
                <SidebarNavItem
                  label={t('shell.nav.support', 'Support')}
                  icon={LifeBuoy}
                  active={currentPage === 'support'}
                  collapsed
                  onClick={() => onNavChange('support')}
                />
                <SidebarNavItem
                  label="Settings"
                  icon={Settings}
                  active={
                    currentPage === 'profile'
                    || currentPage === 'account-settings'
                    || currentPage === 'developer'
                  }
                  collapsed
                  dataTour="tour-account-settings"
                  onClick={() => onNavChange('account-settings')}
                />
              </div>
            </div>
          ) : (
            <>
              {PRIMARY_ITEMS.map(item => (
                <SidebarNavItem
                  key={item.id}
                  label={t(item.labelKey, item.fallback)}
                  icon={item.icon}
                  active={resolvedNavId === item.id}
                  dataTour={item.dataTour}
                  onClick={() => handlePrimaryNav(item.id)}
                />
              ))}

              {featureItems.map(item => (
                <SidebarNavItem
                  key={item.id}
                  label={t(item.labelKey, item.fallback)}
                  icon={item.icon}
                  active={item.path ? window.location.pathname === item.path : currentPage === item.page}
                  dataTour={item.dataTour}
                  onClick={() => handleMoreNav(item)}
                />
              ))}

              <SidebarCollections onNavigate={handleNavChange} />

            </>
          )}
        </nav>

        {!sidebarCollapsed && (
          <SidebarFooter onNavChange={onNavChange} onSignOutClick={onSignOutClick} />
        )}
      </motion.aside>

      {/* ═══════════════════════════════════════════════════════════════════
          RIGHT COLUMN: top bar (h-12, mirrors B2B) + scrollable content
          ═══════════════════════════════════════════════════════════════════ */}
      <div className="flex-1 flex flex-col min-w-0 h-full overflow-hidden">

        {/* Top bar — 48 px, mirrors B2B Topbar exactly:
              left = hamburger (mobile) + breadcrumb (desktop)
              right = credits pill + notifications + avatar pill */}
        <header
          data-tour="topbar"
          className="ds-workspace-topbar flex-shrink-0 flex items-center gap-2 px-3 sm:gap-4 sm:px-5 h-12 min-h-[48px] border-b z-20"
          role="banner"
        >
          {/* Mobile hamburger — opens sheet on mobile only */}
          <button type="button" onClick={() => setMobileMenuOpen(true)}
            className="flex-shrink-0 rounded-[8px] p-2 min-h-[44px] min-w-[44px] ds-workspace-icon-button shrink-0 lg:hidden touch-manipulation"
            aria-label={t('layout.ariaOpenMenu', 'Open menu')}>
            <PanelLeft className="h-5 w-5" aria-hidden />
          </button>

          {/* Mobile page title + agent quick actions */}
          {!isDesktop && (
            <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
              {pageTitle && currentPage !== 'agent' && (
                <span
                  className="truncate text-base font-semibold leading-none"
                  style={{ color: 'var(--ds-text-strong)' }}
                >
                  {pageTitle}
                </span>
              )}
              {currentPage === 'agent' && (
                <div className="agent-topbar-actions flex shrink-0 items-center gap-1">
                  <button
                    type="button"
                    onClick={() => window.dispatchEvent(new CustomEvent(GGJ_AGENT_NEW_SESSION))}
                    className="agent-topbar-actions__btn agent-topbar-actions__btn--primary"
                    title="New chat"
                    aria-label="New chat"
                  >
                    <Plus className="h-[18px] w-[18px]" aria-hidden />
                  </button>
                  <button
                    type="button"
                    onClick={() => window.dispatchEvent(new CustomEvent(GGJ_AGENT_TOGGLE_HISTORY))}
                    className="agent-topbar-actions__btn"
                    title="Chat history"
                    aria-label="Chat history"
                  >
                    <History className="h-[18px] w-[18px]" aria-hidden />
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Breadcrumb */}
          <nav className="min-w-0 flex-1 items-center overflow-hidden hidden lg:flex" aria-label="Breadcrumb">
            <ol className="flex min-w-0 flex-nowrap items-center gap-1.5 overflow-hidden whitespace-nowrap">
              {pageTitle && (
                <li className="flex items-center gap-1.5 shrink-0">
                  <span
                    className={`text-lg font-semibold leading-none ${agentSubTitle ? 'cursor-pointer transition-colors' : ''}`}
                    style={{ color: agentSubTitle ? 'var(--ds-text-muted)' : 'var(--ds-text-strong)' }}
                    onClick={agentSubTitle ? () => setAgentSubTitle('') : undefined}
                  >
                    {pageTitle}
                  </span>
                </li>
              )}
              {agentSubTitle && (
                <>
                  <li className="shrink-0" style={{ color: 'var(--ds-text-muted)' }} aria-hidden>›</li>
                  <li className="min-w-0">
                    <span className="min-w-0 truncate font-semibold block max-w-[280px]" style={{ color: 'var(--ds-text-strong)' }} title={agentSubTitle}>
                      {agentSubTitle}
                    </span>
                  </li>
                </>
              )}
            </ol>
          </nav>

          {/* Right cluster — high-density unified action block */}
          <div data-tour="topbar-right-cluster" className="ds-workspace-topbar-cluster ml-auto flex shrink-0 items-center gap-0 rounded-lg border border-slate-100 bg-white px-0.5 py-0.5 dark:border-slate-700 dark:bg-[var(--ds-bg-card)]">
            <UtilityCluster
              userName={userName}
              userEmail={userEmail}
              userAvatarUrl={userAvatarUrl}
              credits={credits}
              creditsLoading={creditsLoading}
              onUpgradeClick={onUpgradeClick}
              onSignOutClick={onSignOutClick}
              onNavChange={handleNavChange}
              isDesktop={isDesktop}
            />
          </div>
        </header>

        {/* Main scrollable content */}
        <main className="relative flex-1 min-w-0 min-h-0 flex flex-col overflow-hidden">
          <div
            className={`flex-1 min-h-0 flex flex-col ${
              mobileMenuOpen && !isDesktop
                ? 'overflow-hidden'
                : fullBleed || currentPage === 'dashboard'
                  ? 'overflow-hidden'
                  : 'overflow-y-auto overflow-x-hidden'
            } dashboard-scroll-root relative z-0`}
            style={{ pointerEvents: 'auto' }}
          >
            {greetingVisible && (
              <GreetingBlock firstName={firstName} onSearch={onHeroSearch} />
            )}
            {children}
          </div>
        </main>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════
          MOBILE SLIDE-IN SHEET
          ═══════════════════════════════════════════════════════════════════ */}
      {mobileMenuOpen && !isDesktop && (
        <>
          <button
            type="button"
            aria-label={t('layout.ariaCloseMenu', 'Close menu')}
            onClick={() => setMobileMenuOpen(false)}
            className="ds-mobile-sidebar-backdrop fixed inset-0 z-[1015] cursor-pointer border-0 bg-black/55 p-0 touch-manipulation"
          />
          <aside
            className="ds-workspace-sidebar ds-mobile-sidebar-panel fixed top-0 left-0 bottom-0 z-[1020] flex w-[min(85vw,240px)] max-w-[85vw] flex-col border-r shadow-2xl"
            role="dialog"
            aria-modal="true"
            aria-label={t('layout.ariaMobileNav', 'Mobile navigation')}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 h-12 border-b shrink-0"
              style={{ borderColor: 'var(--ds-border)' }}>
              <GGJLogoFull
                iconSize={BRAND_LOGO_DISPLAY.mobileMenuIcon}
                fontSize={BRAND_LOGO_DISPLAY.mobileMenuFont}
                textColor="rgba(255,255,255,0.95)"
              />
              <button type="button" onClick={() => setMobileMenuOpen(false)}
                className="flex items-center justify-center rounded-[8px] p-2 min-h-[44px] min-w-[44px] ds-workspace-icon-button touch-manipulation"
                aria-label={t('layout.ariaCloseMenu', 'Close menu')}>
                <XIcon className="w-5 h-5" />
              </button>
            </div>
            <nav className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto overflow-x-hidden px-3 py-2">
              {PRIMARY_ITEMS.map(item => (
                <SidebarNavItem
                  key={item.id}
                  label={t(item.labelKey, item.fallback)}
                  icon={item.icon}
                  active={resolvedNavId === item.id}
                  dataTour={`${item.dataTour}-mobile`}
                  onClick={() => { handlePrimaryNav(item.id); setMobileMenuOpen(false); }}
                />
              ))}
              {featureItems.map(item => (
                <SidebarNavItem
                  key={item.id}
                  label={t(item.labelKey, item.fallback)}
                  icon={item.icon}
                  active={item.path ? window.location.pathname === item.path : currentPage === item.page}
                  dataTour={item.dataTour ? `${item.dataTour}-mobile` : undefined}
                  onClick={() => { handleMoreNav(item); setMobileMenuOpen(false); }}
                />
              ))}
            </nav>
            <SidebarFooter
              variant="mobile"
              onNavChange={p => { onNavChange(p); setMobileMenuOpen(false); }}
              onSignOutClick={onSignOutClick}
            />
          </aside>
        </>
      )}

      {!completionProfileLoading && completionProfile && !agentCanvasOpen && currentPage !== 'agent' && currentPage !== 'ai-companion' && currentPage !== 'history' && currentPage !== 'referrals' && currentPage !== 'support' && currentPage !== 'product-feedback' && currentPage !== 'account-settings' && (
        <div
          className="pointer-events-none fixed inset-x-0 bottom-0 z-[1100] flex justify-end max-md:items-stretch"
          style={{
            paddingRight: 'max(1rem, env(safe-area-inset-right))',
            paddingBottom: 'max(1rem, env(safe-area-inset-bottom))',
            paddingLeft: 'max(1rem, env(safe-area-inset-left))',
          }}
        >
          <div className="pointer-events-auto max-md:w-full">
            <ProfileCompletionWidget
              profile={completionProfile}
              onNavigate={handleProfileCompletionNavigate}
            />
          </div>
        </div>
      )}

      {/* ── Tour welcome prompt (shown once after onboarding, before tour begins) ── */}
      <AnimatePresence>
        {showTourPrompt && (
          <>
            {/* Backdrop */}
            <motion.div
              key="tour-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[9990] bg-black/50 backdrop-blur-sm"
              onClick={() => {
                setShowTourPrompt(false);
                try { localStorage.setItem('ggj:tour-prompt-dismissed', '1'); } catch { /* */ }
              }}
            />
            {/* Card */}
            <motion.div
              key="tour-card"
              initial={{ opacity: 0, scale: 0.92, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
              className="fixed inset-0 z-[9991] flex items-center justify-center px-4 pointer-events-none"
            >
              <div
                className="pointer-events-auto w-full max-w-sm rounded-2xl overflow-hidden shadow-2xl"
                style={{
                  background:  'var(--ds-bg-card)',
                  border:      '1px solid var(--ds-brand-border)',
                  boxShadow:   '0 0 0 1px rgba(108,71,255,0.1), 0 24px 80px rgba(0,0,0,0.5)',
                }}
              >
                {/* Brand header */}
                <div
                  className="px-6 py-4"
                  style={{ background: 'var(--ds-brand)' }}
                >
                  <p className="t-label mb-0.5" style={{ color: 'rgba(255,255,255,0.75)' }}>
                    You&apos;re all set
                  </p>
                  <h2 className="t-h2 leading-snug" style={{ color: '#fff' }}>
                    Want a quick walkthrough?
                  </h2>
                </div>

                {/* Body */}
                <div className="px-6 py-5">
                  <p
                    className="t-body leading-relaxed mb-5"
                    style={{ color: 'var(--ds-text-body)' }}
                  >
                    Takes about 2 minutes. We&apos;ll show you every tool — jobs, AI agent, resume
                    optimizer, skill assessments, and more. You can replay it any time from Account
                    Settings.
                  </p>

                  <div className="flex flex-col gap-2.5">
                    <button
                      type="button"
                      onClick={() => {
                        setShowTourPrompt(false);
                        try { localStorage.setItem('ggj:tour-prompt-dismissed', '1'); } catch { /* */ }
                        setTimeout(() => forceProductTour(), 150);
                      }}
                      className="w-full py-2.5 rounded-xl t-body font-semibold transition-opacity hover:opacity-90"
                      style={{ background: 'var(--ds-brand)', color: '#fff' }}
                    >
                      Show me around
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowTourPrompt(false);
                        try { localStorage.setItem('ggj:tour-prompt-dismissed', '1'); } catch { /* */ }
                      }}
                      className="w-full py-2.5 rounded-xl t-body font-medium transition-colors"
                      style={{ color: 'var(--ds-text-muted)' }}
                      onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--ds-text-body)')}
                      onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--ds-text-muted)')}
                    >
                      Maybe later — I&apos;ll explore myself
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default WorkspaceShell;
